<template>
  <AppStack
    direction="flex-col"
    class="px-4"
    :class="props.hasMultipleOrders ? ['pb-6'] : []"
  >
    <AppStack v-if="!props.batchItems?.length" direction="flex-col">
      <AppStack align-items="items-baseline" class="mb-[10px]">
        <AppStack align-items="items-baseline">
          <AppTypography
            :text="formatPrice(orderTotal.toFixed(2))"
            name="heading-2xl-8-bold"
            color="text-fg-elevation-2-primary"
          />
        </AppStack>
        <AppStack
          v-if="
            Object.keys(props.order?.appliedPromotions?.promotions || {}).length
          "
          align-items="items-center"
          class="ml-auto"
        >
          <AppTypography
            :text="formatPrice(-promotionsTotalDiscount)"
            name="text-lg-7-medium"
            color="text-fg-elevation-2-secondary"
          />
          <AppIcon
            icon="percentage"
            size="custom"
            color="text-fg-elevation-2-secondary"
            stroke-width="1.8px"
            class="w-[22px] h-[22px] ml-1"
          />
        </AppStack>
      </AppStack>
    </AppStack>
    <MoveItemsActionBar v-if="moveFlowIsRunning" />
    <AppStack v-else-if="!props.batchItems?.length" class="gap-2">
      <AppButton
        :disabled="checkoutIsDisabled"
        :text="$t('order.checkout')"
        variant="secondary-blue"
        class="flex-1"
        @click="emit('checkout-tap')"
      />
      <AppButton variant="outline-secondary" @click="emit('actions-tap')">
        <AppIcon
          color="text-fg-elevation-3-secondary"
          icon="more-horiz"
          size="custom"
          stroke-width="2px"
          class="w-[30px] h-[30px] relative"
        />
      </AppButton>
    </AppStack>
    <AppStack v-else class="gap-2">
      <AppButton :text="$t('order.sendToWork')" class="flex-1" @click="emit('send-tap')" />
      <AppButton variant="outline-secondary" @click="emit('clear-tap')">
        <AppIcon
          color="text-fg-elevation-3-secondary"
          icon="bin-minus"
          size="custom"
          class="w-[30px] h-[30px] relative"
        />
      </AppButton>
    </AppStack>
  </AppStack>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '@restify/packages/design-system/low-level/AppIcon.vue'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppTypography from '@restify/packages/design-system/low-level/AppTypography.vue'
import AppButton from '~/components/AppButton.vue'
import MoveItemsActionBar from '~/components/MoveItemsActionBar.vue'
import useAppHelpers from '~/composables/useAppHelpers'
import { useInjectMoveItemsState } from '@restify/packages/composables/useMoveItemsState'
import { type Stores } from '~/composables/useStores'

const emit = defineEmits<{
  'actions-tap': [],
  'checkout-tap': [],
  'clear-tap': [],
  'send-tap': [],
}>()
const props = defineProps<{
  order?: Stores['orders']['Result'] | null
  batchItems?: unknown[]
  sendToWorkInProgress?: boolean
  hasMultipleOrders?: boolean
}>()

const { formatPrice, countPromotionTotal } = useAppHelpers()

// Checkout is available once the order has at least one menu item sent
// to work — there's nothing meaningful to pay for an empty order.
// Server-side checkout state machine also rejects cancelled/checked-out
// orders, but we gate at UI level to avoid the round-trip.
const checkoutIsDisabled = computed(() => {
  const status = props.order?.status?.name
  if (status === 'cancelled' || status === 'checked-out') return true

  return !(props.order?.menuItems?.length)
})

const orderTotal = computed(() => {
  return typeof props?.order?.appliedPromotions?.total === 'number'
    ? props.order?.appliedPromotions?.total || 0
    : props.order?.total || 0
})

const promotionsTotalDiscount = computed(() => {
  return props.order ? countPromotionTotal(props.order) : 0
})

const { moveItemsSourceOrderId } = useInjectMoveItemsState()

// Show the move-items row on ANY open order while a move-flow is
// running — not just the source. The manager needs the Submit button
// on the target too (that's where they land after picking the
// destination table).
const moveFlowIsRunning = computed(() => !!moveItemsSourceOrderId.value)
</script>
